/**
 * Part 8 — Enterprise Trust service.
 *
 * Wraps every endpoint added by Part 8:
 *   - Auth policy (MFA / session / password / IP allowlist)
 *   - SSO configurations (OIDC + SAML)
 *   - SCIM admin tokens
 *   - Audit log (paginated + JSON/CSV export + chain verify)
 *   - Tenant data export + deletion request
 *   - MFA enroll / activate / verify / disable
 *   - Refresh + logout
 */
import { apiClient } from './api'

export interface AuthPolicy {
  mfa_required: boolean
  session_timeout_minutes: number
  password_min_length: number
  password_require_upper: boolean
  password_require_lower: boolean
  password_require_digit: boolean
  password_require_symbol: boolean
  password_rotation_days: number | null
  ip_allowlist: string[] | null
}

export interface SSOConfig {
  id: number
  protocol: 'oidc' | 'saml'
  name: string
  is_active: boolean
  oidc_issuer?: string | null
  oidc_client_id?: string | null
  oidc_client_secret?: string | null
  oidc_redirect_uri?: string | null
  oidc_scopes?: string | null
  saml_entity_id?: string | null
  saml_sso_url?: string | null
  saml_sp_entity_id?: string | null
  saml_sp_acs_url?: string | null
  saml_x509_cert?: string | null
  attribute_map?: Record<string, string> | null
  group_map?: Record<string, number> | null
  just_in_time_provisioning: boolean
  has_client_secret?: boolean
  has_x509_cert?: boolean
}

export interface AuditEntry {
  id: number
  org_id: number | null
  user_id: number | null
  entity_type: string
  entity_id: number | null
  action: string
  description: string | null
  extra_data: Record<string, unknown> | null
  created_at: string | null
  prev_hash: string | null
  this_hash: string | null
}

export interface SCIMTokenInfo {
  id: number
  name: string
  is_active: boolean
  last_used_at: string | null
  created_at: string | null
}

export interface DeletionStatus {
  status: 'none' | 'pending' | 'cancelled' | 'executed'
  scheduled_for: string | null
  executed_at: string | null
  cancelled_at: string | null
  reason: string | null
}

export const enterpriseTrustService = {
  // ── Auth policy ───────────────────────────────────────────────────
  getAuthPolicy: () => apiClient.get<AuthPolicy>('/api/v1/auth-policies/').then(r => r.data),
  saveAuthPolicy: (p: AuthPolicy) =>
    apiClient.put<AuthPolicy>('/api/v1/auth-policies/', p).then(r => r.data),

  // ── SSO configs ───────────────────────────────────────────────────
  listSSO: () => apiClient.get<SSOConfig[]>('/api/v1/sso/configurations').then(r => r.data),
  createSSO: (cfg: Partial<SSOConfig>) =>
    apiClient.post<SSOConfig>('/api/v1/sso/configurations', cfg).then(r => r.data),
  updateSSO: (id: number, cfg: Partial<SSOConfig>) =>
    apiClient.patch<SSOConfig>(`/api/v1/sso/configurations/${id}`, cfg).then(r => r.data),
  deleteSSO: (id: number) =>
    apiClient.delete(`/api/v1/sso/configurations/${id}`).then(() => undefined),
  oidcAuthorize: (id: number) =>
    apiClient.get<{ authorize_url: string }>(`/api/v1/sso/oidc/${id}/login`).then(r => r.data),

  // ── SCIM tokens ───────────────────────────────────────────────────
  listSCIMTokens: () => apiClient.get<SCIMTokenInfo[]>('/api/v1/scim-tokens/').then(r => r.data),
  createSCIMToken: (name: string) =>
    apiClient.post<{ id: number; name: string; token: string }>(
      '/api/v1/scim-tokens/', { name }
    ).then(r => r.data),
  revokeSCIMToken: (id: number) =>
    apiClient.delete(`/api/v1/scim-tokens/${id}`).then(() => undefined),

  // ── Audit log ─────────────────────────────────────────────────────
  listAudit: (params: { skip?: number; limit?: number; entity_type?: string; action?: string } = {}) =>
    apiClient.get<{ items: AuditEntry[]; count: number }>('/api/v1/audit/log', { params })
      .then(r => r.data),
  verifyAudit: () =>
    apiClient.get<{ ok: boolean; entries: number; mismatch_at: number | null }>(
      '/api/v1/audit/verify',
    ).then(r => r.data),
  exportAudit: async (fmt: 'json' | 'csv'): Promise<Blob> => {
    const r = await apiClient.get(`/api/v1/audit/export.${fmt}`, { responseType: 'blob' })
    return r.data as Blob
  },

  // ── Tenant data ───────────────────────────────────────────────────
  exportTenant: async (): Promise<Blob> => {
    const r = await apiClient.get('/api/v1/tenant-data/export', { responseType: 'blob' })
    return r.data as Blob
  },
  requestTenantDeletion: (data: { reason?: string; grace_period_days?: number }) =>
    apiClient.post('/api/v1/tenant-data/delete', { confirmation: 'DELETE', ...data })
      .then(r => r.data),
  cancelTenantDeletion: () =>
    apiClient.post('/api/v1/tenant-data/delete/cancel').then(r => r.data),
  tenantDeletionStatus: () =>
    apiClient.get<DeletionStatus>('/api/v1/tenant-data/delete/status').then(r => r.data),

  // ── MFA ───────────────────────────────────────────────────────────
  mfaEnroll: () =>
    apiClient.post<{ provisioning_uri: string; secret: string }>('/api/v1/auth/mfa/enroll')
      .then(r => r.data),
  mfaActivate: (code: string) =>
    apiClient.post<{ activated: boolean; backup_codes: string[] }>(
      '/api/v1/auth/mfa/activate', { code }
    ).then(r => r.data),
  mfaVerify: (challenge_token: string, code: string) =>
    apiClient.post('/api/v1/auth/mfa/verify', { challenge_token, code }).then(r => r.data),
  mfaDisable: () => apiClient.post('/api/v1/auth/mfa/disable').then(() => undefined),

  // ── Tokens ────────────────────────────────────────────────────────
  refresh: (refresh_token: string) =>
    apiClient.post('/api/v1/auth/refresh', { refresh_token }).then(r => r.data),
  logout: (refresh_token?: string) =>
    apiClient.post('/api/v1/auth/logout', { refresh_token }).then(() => undefined),
}

// ── Part 8b — Zero-Trust Completeness ───────────────────────────────────────

export interface SOC2EvidenceBundle {
  id: number
  sha256: string
  size_bytes: number
  generated_by: number | null
  generated_at: string | null
  notes: string | null
}

export const zeroTrustService = {
  /** A8b.7 — download a fresh SOC2 evidence ZIP. */
  downloadEvidencePackage: async (): Promise<{ blob: Blob; sha256: string }> => {
    const r = await apiClient.get('/api/v1/soc2/evidence-package', {
      responseType: 'blob',
    })
    return {
      blob: r.data as Blob,
      sha256: (r.headers['x-evidence-sha256'] as string) || '',
    }
  },
  listEvidenceBundles: () =>
    apiClient.get<SOC2EvidenceBundle[]>('/api/v1/soc2/evidence-bundles')
      .then(r => r.data),

  /** A8b.5 — step-up re-auth. method = 'password' | 'totp'. */
  confirmStepUp: (method: 'password' | 'totp', secret: string) =>
    apiClient.post('/api/v1/auth/confirm-step-up',
      method === 'password' ? { method, password: secret } : { method, code: secret },
    ).then(r => r.data),
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
